import { ORDER_STATUS_TONES, type OrderView } from '@khaddi/contracts';
import { Badge, Button, colors, Cup, ErrorNote, formatDateTime, formatVnd, Grid, Icon, Screen, Spinner, Text, TopBar, useApi, useAsync, useLayout, useRouter } from '@khaddi/ui';
import { Pressable, View } from 'react-native';
import { useTabNav } from '../components/TabNav';

const isOpen = (o: OrderView) => o.status !== 'completed' && o.status !== 'cancelled';

export const OrdersScreen = () => {
  const nav = useTabNav('orders');
  const columns = useLayout().wide ? 2 : 1;
  const api = useApi();
  const { navigate } = useRouter();
  // Light polling so a status change shows up without pulling to refresh.
  const { data, error, loading, reload } = useAsync(() => api.orders.mine(), [api], { pollMs: 15000 });
  const orders = data ?? [];
  const active = orders.filter(isOpen);
  const past = orders.filter((o) => !isOpen(o));

  return (
    <Screen nav={nav}>
      <TopBar title="Đơn hàng" />
      {loading && !data ? <Spinner /> : null}
      {error && !data ? <ErrorNote message={error} onRetry={() => void reload()} /> : null}
      {data && orders.length === 0 ? (
        <View style={{ padding: 24, alignItems: 'center', gap: 12, width: '100%', maxWidth: 480, alignSelf: 'center' }}>
          <Cup tint={colors.sky} width={88} />
          <Text variant="title">Chưa có đơn nào</Text>
          <Text variant="caption" style={{ textAlign: 'center' }}>
            Đặt ly đầu tiên để theo dõi đơn và tích điểm tại đây.
          </Text>
          <Button label="Xem thực đơn" onPress={() => navigate('/menu')} style={{ alignSelf: 'stretch' }} />
        </View>
      ) : null}
      <View style={{ padding: 16, gap: 12 }}>
        {active.length ? <Text variant="heading">Đang xử lý</Text> : null}
        <Grid columns={columns}>
          {active.map((o) => (
            <OrderRow key={o.id} order={o} onPress={() => navigate(`/orders/${encodeURIComponent(o.id)}`)} />
          ))}
        </Grid>
        {past.length ? <Text variant="heading" style={{ marginTop: active.length ? 8 : 0 }}>Đơn trước đây</Text> : null}
        <Grid columns={columns}>
          {past.map((o) => (
            <OrderRow key={o.id} order={o} onPress={() => navigate(`/orders/${encodeURIComponent(o.id)}`)} />
          ))}
        </Grid>
      </View>
    </Screen>
  );
};

const OrderRow = ({ order, onPress }: { order: OrderView; onPress: () => void }) => {
  const tone = ORDER_STATUS_TONES[order.status];
  const first = order.lines[0];
  return (
    <Pressable
      accessibilityRole="link"
      accessibilityLabel={`Đơn ${order.code}, ${order.statusLabel}, ${formatVnd(order.pricing.total)}`}
      onPress={onPress}
      style={({ pressed }) => ({ backgroundColor: colors.surface, borderRadius: 18, padding: 14, flexDirection: 'row', gap: 12, alignItems: 'center', transform: [{ scale: pressed ? 0.98 : 1 }] })}
    >
      <View style={{ width: 48, height: 48, borderRadius: 12, backgroundColor: first?.background ?? colors.surfaceMuted, alignItems: 'center', justifyContent: 'flex-end' }}>
        <Cup tint={first?.tint ?? colors.sky} width={30} variant="thumb" />
      </View>
      <View style={{ flex: 1, gap: 4 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', columnGap: 8, rowGap: 4 }}>
          <Text variant="bodyStrong" numberOfLines={1}>
            #{order.code}
          </Text>
          <Badge label={order.statusLabel} background={tone.bg} color={tone.fg} />
        </View>
        <Text variant="caption" numberOfLines={1}>
          {order.itemsSummary}
        </Text>
        <Text variant="label" style={{ fontWeight: '400' }}>
          {formatDateTime(order.createdAt)} · {order.channelLabel}
        </Text>
      </View>
      <View style={{ alignItems: 'flex-end', gap: 4 }}>
        <Text variant="bodyStrong" numeric>
          {formatVnd(order.pricing.total)}
        </Text>
        <Icon name="chevronRight" size={18} color={colors.textMuted} />
      </View>
    </Pressable>
  );
};
